import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  abarbeiten, beobachten, eintraege, nochmal, verwerfen,
  warteschlangeStarten, type Eintrag, type Stand
} from "./warteschlange";

/**
 * Der Balken, der sagt, wo die Anwendung gerade steht.
 *
 * Er ist die einzige Stelle, an der ein Mitarbeiter erfaehrt, dass seine
 * Eingaben noch nicht beim Buero sind. Ohne ihn waere die Warteschlange eine
 * Blackbox, und eine Blackbox, die Arbeitszeit und Pruefberichte enthaelt,
 * will niemand haben.
 *
 * Drei Zustaende, in dieser Rangfolge:
 *
 *   abgelehnt   Rot. Der Server hat etwas dauerhaft zurueckgewiesen. Das muss
 *               ein Mensch ansehen, deshalb steht es vor allem anderen.
 *   ohne Netz   Bernstein. Kein Grund zur Sorge, aber gut zu wissen: was jetzt
 *               eingegeben wird, liegt vorerst auf dem Geraet.
 *   wartet      Ruhig. Es gibt Netz und die Schlange arbeitet.
 *
 * Ist nichts davon der Fall, erscheint gar nichts. Ein dauerhaft gruener
 * Balken "alles in Ordnung" wird nach drei Tagen nicht mehr gelesen.
 */

export function NetzBalken() {
  const { t } = useTranslation();
  const [stand, setStand] = useState<Stand | null>(null);
  const [offen, setOffen] = useState(false);
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  useEffect(() => {
    warteschlangeStarten();
    const abmelden = beobachten(setStand);
    const an = () => setOnline(true);
    const aus = () => setOnline(false);
    window.addEventListener("online", an);
    window.addEventListener("offline", aus);
    return () => {
      abmelden();
      window.removeEventListener("online", an);
      window.removeEventListener("offline", aus);
    };
  }, []);

  const wartend = stand?.offen ?? 0;
  const abgelehnt = stand?.fehlerhaft ?? 0;
  // Nichts zu melden heisst: nichts anzeigen.
  if (online && wartend === 0 && abgelehnt === 0) return null;

  const farbe = abgelehnt > 0 ? "bg-red-700 text-white" : "bg-marke text-tinte";

  return (
    <>
      <div className={`flex flex-wrap items-center justify-center gap-3 px-4 py-2 text-sm ${farbe}`}>
        {abgelehnt > 0 ? (
          <>
            <span className="font-semibold">{t("netz.abgelehnt", { count: abgelehnt })}</span>
            <button className="underline" onClick={() => setOffen(true)}>{t("netz.anzeigen")}</button>
          </>
        ) : !online ? (
          <span>{t("netz.ohneNetz")}</span>
        ) : null}

        {wartend > 0 && (
          <>
            <span>{t("netz.wartet", { count: wartend })}</span>
            {online && (
              <button className="underline" onClick={() => void abarbeiten()}>
                {stand?.laeuft ? t("netz.sendet") : t("netz.jetztSenden")}
              </button>
            )}
          </>
        )}
      </div>

      {offen && <AbgelehntDialog onSchliessen={() => setOffen(false)} />}
    </>
  );
}

/**
 * Was der Server zurueckgewiesen hat, im Klartext.
 *
 * Mit genau zwei Moeglichkeiten: noch einmal versuchen, wenn der Grund behoben
 * ist, oder verwerfen. Verworfen wird nur nach einer Rueckfrage, denn danach
 * ist die Eingabe endgueltig weg.
 */
function AbgelehntDialog({ onSchliessen }: { onSchliessen: () => void }) {
  const { t } = useTranslation();
  const [liste, setListe] = useState<Eintrag[] | null>(null);

  const laden = () => void eintraege().then((e) => setListe(e.filter((x) => x.fehler)));
  useEffect(laden, []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-tinte/60 p-4">
      <div className="mt-10 w-full max-w-lg rounded-card bg-white p-5">
        <h2 className="mb-3 font-bold">{t("netz.abgelehntTitel")}</h2>

        {liste === null ? null : liste.length === 0 ? (
          <p className="text-sm text-leise">{t("netz.nichtsAbgelehnt")}</p>
        ) : (
          <ul className="space-y-3">
            {liste.map((e) => (
              <li key={e.id} className="rounded border border-linie/30 p-3">
                <p className="font-semibold">{e.beschreibung}</p>
                <p className="mt-1 text-sm text-red-700">{e.fehler}</p>
                <p className="mt-1 text-xs text-leise">
                  {t("netz.versuche", { count: e.versuche })}
                </p>
                <div className="mt-2 flex gap-3 text-sm">
                  <button
                    className="underline"
                    onClick={() => { void nochmal(e.id).then(laden); }}
                  >
                    {t("netz.nochmal")}
                  </button>
                  <button
                    className="text-red-700 underline"
                    onClick={() => {
                      if (window.confirm(t("netz.verwerfenFrage"))) {
                        void verwerfen(e.id).then(laden);
                      }
                    }}
                  >
                    {t("netz.verwerfen")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 text-right">
          <button className="text-sm underline" onClick={onSchliessen}>{t("netz.schliessen")}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Der Hinweis auf einer Seite, die aus der Kopie auf dem Geraet kommt.
 *
 * Steht ueber dem Inhalt und nennt den Zeitpunkt. Wer am Objekt eine
 * Checkliste oeffnet, muss wissen, ob er den Stand von heute Morgen sieht.
 */
export function AblageHinweis({ stand }: { stand: string | null }) {
  const { t } = useTranslation();
  if (!stand) return null;
  const zeit = new Date(stand).toLocaleString();
  return (
    <p className="mb-4 rounded border border-marke bg-marke/10 px-3 py-2 text-sm">
      {t("netz.ausAblage", { zeit })}
    </p>
  );
}
